import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as uuid from 'uuid';
import { BehaviorSubject, defer, Observable, Subscription } from 'rxjs';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { TakeOverPanelComponent } from '../../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { TopicsService } from '../../../Core/services/topics.service';
import { MentionSearchModel } from '../../models/mention-search.model';

/**
 * Hosts the full topic editor ({@link MonitoringSearchComponent}) inside a takeover panel so an
 * existing saved search can be edited in place instead of navigating to the update-topic route.
 * The center panel, right-hand panel and action bar are reused as-is via `panelMode`.
 */
@Component({
    selector: 'agpr-edit-topic-take-over-panel',
    templateUrl: './edit-topic-take-over-panel.component.html',
    styleUrls: ['./edit-topic-take-over-panel.component.scss'],
    standalone: false
})
export class EditTopicTakeOverPanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    overlayContainer: Element;
    animationTrigger: BehaviorSubject<FullProfileAnimationModel>;

    @ViewChild('takeOverPanel') takeOverPanel: TakeOverPanelComponent;

    hideSlideOverlay: boolean = false;
    panelTitle: string = '';

    // Gates the panel close on the standard unsaved-changes confirmation (mirrors TopicDeactivateGuard).
    // Re-evaluated on every close attempt because the panel re-subscribes each time close() runs.
    readonly exitValidation$: Observable<boolean> = defer(() => this.topicsService.showExitDialog());

    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private topicsService: TopicsService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, uuid.v4());
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.topicsService.getFocusedTopic().subscribe((topic: MentionSearchModel) => {
                if (topic) {
                    // A saved topic shows "Edit <name>"; an ad-hoc search shows "Edit Search".
                    this.panelTitle = topic.name
                        ? this.localizationService.transform('Monitoring.search.editTopicTitle', { topicName: topic.name })
                        : this.localizationService.transform('Monitoring.search.editSearchTitle');
                }
            })
        );
    }

    /**
     * Routes the X button, ESC and click-outside through the panel's validateBeforeClose gate so
     * unsaved changes prompt for confirmation before the overlay tears down.
     */
    requestClose(): void {
        this.takeOverPanel?.close();
    }

    /**
     * Fired once the slide-out animation completes (X / ESC / click-outside). Removes the dialog and
     * restores the focused topic to its cached (last-saved or original) state, discarding any unsaved
     * in-memory edits so a later edit/refresh shows the original rather than the modified version.
     * The Search action closes via {@link onSearchRequested} instead and so is not reset here.
     */
    onVisibleChange(isVisible: boolean): void {
        if (!isVisible) {
            this.closeDialog();
            this.topicsService.resetTopicToCache();
        }
    }

    /**
     * The Search action applies the search and navigates to the results; that navigation tears the
     * panel down via CoreDialogService's NavigationStart handler. Closing here also covers the
     * same-route case where no navigation event fires.
     */
    onSearchRequested(): void {
        this.closeDialog();
    }

    // Override the inherited ESC handler so it respects the unsaved-changes gate instead of closing immediately.
    override onKeyDown(): void {
        const lastDialog = this.coreDialogService.getLastDialog();
        if (this.key === lastDialog?.instance.key) {
            this.requestClose();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
